// src/features/media/components/OverviewTab.tsx

import { Card, Group, Text, Button, Stack, Box } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { VideoFormSchema, type VideoFormData } from '../schemas/VideoForm.schema';
import { useUpdateVideo } from '../useVideoMutations';
import { MediaUtils } from '../MediaUtils';
import { VideoGeneralInfoForm } from './VideoGeneralInfoForm';
import { useTags } from '@/features/tag/useTags';
import { useRefIdConflict } from '@/shared/hooks/useRefIdConflict';
import type { VideoViewData } from '@/lib/types/Media';

interface OverviewTabProps {
  video: VideoViewData;
}

export function OverviewTab({ video }: OverviewTabProps) {
  const mediaId = video.media.mediaId;
  const updateMutation = useUpdateVideo(mediaId);
  const { tags: allTags } = useTags();

  const { control, watch, handleSubmit, formState: { isDirty, errors }, reset } = useForm<VideoFormData>({
    resolver: zodResolver(VideoFormSchema),
    defaultValues: MediaUtils.transformVideoToFormData(video),
  });

  const originalRefId = video.media.refId ?? '';
  const { conflict, conflictMessage, isChecking } = useRefIdConflict({
    refId: watch('refId'),
    originalRefId,
    selfScope: 'media',
    selfId: mediaId,
  });

  const onSubmit = async (data: VideoFormData) => {
    // Belt-and-braces for Enter-key submits; the Save button is already
    // disabled while a conflict is shown. Server validation stays authoritative.
    if (conflict) {
      notifications.show({
        title: 'Reference ID conflict',
        message: conflictMessage,
        color: 'orange',
      });
      return;
    }
    const refIdChanged = data.refId !== originalRefId;

    try {
      await updateMutation.mutateAsync(
        MediaUtils.transformFormToUpdateRequest(data, refIdChanged)
      );
      reset(data);
      notifications.show({
        title: 'Saved',
        message: `Updated "${data.title}"`,
        color: 'green',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({
        title: 'Validation Error',
        message: firstError.message,
        color: 'orange',
      });
    }
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        {/* Sticky action bar — YouTube Studio / Stripe pattern */}
        {/* top = header height (60px) + AppShell padding (md = 16px) */}
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">General Information</Text>
            <Group gap="sm">
              {isDirty && (
                <Button variant="default" onClick={() => reset()} disabled={isPending}>
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty || conflict != null} loading={isPending}>
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        {/* Form card */}
        <Card shadow="sm" padding="lg" withBorder>
          <VideoGeneralInfoForm
            control={control}
            watch={watch}
            video={video}
            allTags={allTags}
            refIdConflictError={conflictMessage}
            refIdChecking={isChecking}
          />
        </Card>
      </Stack>
    </form>
  );
}
